import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AUTH_ERROR_MESSAGES, type AuthUser, type ReconnexionInfo } from '@authsemply/shared';
import { VersionApp } from '../components/VersionApp';
import { AuthCard } from '../components/AuthCard';
import { Field } from '../components/Field';
import { Alert } from '../components/Alert';
import { SocialButtons } from '../components/SocialButtons';
import { api, ApiError, resumeAuthorizeUrl } from '../lib/api';
import { postLoginDestination, suiteInterne } from '../lib/products';
import { useAuthStore } from '../store/auth.store';

type LoginResponse =
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'mfa_required'; mfaToken: string };

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);
  const setMfaToken = useAuthStore((state) => state.setMfaToken);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Présents quand /oidc/authorize nous a renvoyé ici : on reprend le flux
  // OIDC une fois la session ouverte.
  const returnTo = resumeAuthorizeUrl(searchParams);
  const flowParams = searchParams.toString();
  // Page du portail à rejoindre après la connexion (liste fermée), y compris
  // au retour de Google, Microsoft ou Apple — l'API n'y renvoie que sur le
  // portail lui-même.
  const suite = suiteInterne(searchParams);
  const retourFournisseur = returnTo ?? (suite ? `${window.location.origin}${suite}` : null);

  // Reconnexion exigée par un produit (action sensible) : `prompt=login`. Si
  // la session du portail est toujours là, l'écran devient « Confirmez votre
  // identité » — le compte est imposé, seuls SES moyens sont proposés, rien
  // pour changer de compte ou en créer un. Sans session : connexion ordinaire.
  const demandeReconnexion = searchParams.get('prompt') === 'login';
  const [reconnexion, setReconnexion] = useState<ReconnexionInfo | null>(null);
  const [chargement, setChargement] = useState(demandeReconnexion);

  useEffect(() => {
    if (!demandeReconnexion) return;
    let vivant = true;
    api
      .get<ReconnexionInfo>('/oauth/reconnexion')
      .then((info) => {
        if (vivant) setReconnexion(info);
      })
      .catch(() => undefined)
      .finally(() => {
        if (vivant) setChargement(false);
      });
    return () => {
      vivant = false;
    };
  }, [demandeReconnexion]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);

    const form = new FormData(event.currentTarget);
    try {
      const result = await api.post<LoginResponse>('/auth/login', {
        email: form.get('email'),
        password: form.get('password'),
      });

      if (result.status === 'mfa_required') {
        setMfaToken(result.mfaToken);
        navigate(`/mfa${flowParams ? `?${flowParams}` : ''}`);
        return;
      }

      setUser(result.user);

      // Appairage obligatoire : la session est ouverte, mais le serveur exige
      // un second facteur de ce compte. On passe par la porte AVANT de
      // rejoindre la destination — les paramètres du flux OIDC voyagent avec,
      // pour que la reprise se fasse une fois le téléphone appairé.
      if (result.user.mfaRequired) {
        navigate(`/double-authentification${flowParams ? `?${flowParams}` : ''}`);
        return;
      }

      if (returnTo) {
        window.location.assign(returnTo);
        return;
      }
      const destination = await postLoginDestination(searchParams);
      if ('external' in destination) window.location.assign(destination.external);
      else navigate(destination.internal);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'server_error';
      // Adresse jamais confirmée (27/09/2026) : le compte ne s'ouvre pas, on
      // montre l'écran qui permet de recevoir un nouveau lien, et rien d'autre.
      if (code === 'email_not_verified') {
        navigate(`/confirmer-adresse?${new URLSearchParams({ email: String(form.get('email') ?? '') })}`);
        return;
      }
      setError(AUTH_ERROR_MESSAGES[code as keyof typeof AUTH_ERROR_MESSAGES] ?? 'Connexion impossible.');
    } finally {
      setPending(false);
    }
  }

  if (chargement) {
    return (
      <AuthCard title="Confirmez votre identité" subtitle="Un instant…">
        <p />
      </AuthCard>
    );
  }

  if (reconnexion) {
    return (
      <AuthCard
        title="Confirmez votre identité"
        subtitle="Cette action demande une vérification récente de votre identité."
      >
        <Alert message={error} />
        <p className="subtitle" style={{ marginBottom: '1rem' }}>
          Compte : <strong>{reconnexion.email}</strong>
        </p>
        {reconnexion.password ? (
          <form onSubmit={handleSubmit}>
            <input type="hidden" name="email" value={reconnexion.email} />
            <Field
              label="Mot de passe"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              autoFocus
            />
            <button className="btn" type="submit" disabled={pending}>
              {pending ? 'Vérification…' : 'Confirmer'}
            </button>
          </form>
        ) : null}

        <SocialButtons
          mode="reconnexion"
          returnTo={returnTo}
          providers={reconnexion.providers}
          sansSeparateur={!reconnexion.password}
        />

        <div className="links">
          <button type="button" className="link-button" onClick={() => window.history.back()}>
            Annuler
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Connexion" subtitle="Valable sur l'ensemble des outils SEmply.">
      <Alert message={error} />
      <form onSubmit={handleSubmit}>
        <Field label="Adresse e-mail" name="email" type="email" autoComplete="email" required />
        <Field
          label="Mot de passe"
          name="password"
          type="password"
          autoComplete="current-password"
          required
        />
        <button className="btn" type="submit" disabled={pending}>
          {pending ? 'Connexion…' : 'Se connecter'}
        </button>
      </form>

      <SocialButtons mode="login" returnTo={retourFournisseur} />

      <div className="links">
        <Link to="/mot-de-passe-oublie">Mot de passe oublié</Link>
        <Link to={`/register${flowParams ? `?${flowParams}` : ''}`}>
          Créer un compte
        </Link>
      </div>
      <VersionApp produit="SEmply" />
    </AuthCard>
  );
}
